<!-- eslint-disable -->
<template>
  <transition name="crm-modal-fade">
    <div
      v-if="visible"
      class="crm-modal-backdrop"
      @mousedown.self="$emit('close')"
    >
      <div class="crm-modal-card crm-add-modal">
        <!-- Header -->
        <div class="crm-modal-header">
          <div class="crm-modal-header__left">
            <div class="crm-modal-header__icon">
              <feather-icon icon="PlusCircleIcon" size="18" />
            </div>
            <h3 class="crm-modal-header__title">Add New {{ activePipelineName }}</h3>
          </div>
          <button class="crm-modal-close" @click="$emit('close')">
            <feather-icon icon="XIcon" size="18" />
          </button>
        </div>

        <!-- Body -->
        <div class="crm-modal-body">
          <!-- Title -->
          <div class="crm-form-group">
            <label class="crm-form-label">Title <span class="crm-required">*</span></label>
            <input
              type="text"
              class="crm-input"
              v-model="form.title"
              placeholder="Enter title…"
              ref="titleInput"
            />
          </div>

          <!--
            Karim, 7 Sep: "when creating new item it should pull items from
            the leads not the customers." A New Deal starts from a LEAD you
            are already working, not a fresh company/contact search — that
            search bypassed the lead entirely and left campaign/source/CR
            number blank (only lead conversion ever filled those — see the
            BRD §3.2 note below). Renewals/Tickets are not lead-driven
            (renewals come from expiring certificates, tickets from
            complaints), so they keep the company/contact picker.
          -->
          <div v-if="isDeals" class="crm-form-group">
            <label class="crm-form-label">Lead <span class="crm-required">*</span></label>
            <v-select
              v-model="selectedLead"
              :options="leadOptions"
              label="label"
              :filterable="false"
              :loading="leadLoading"
              placeholder="Type to search your leads…"
              class="crm-vselect"
              @search="onLeadSearch"
              @input="onLeadPicked"
            >
              <template #no-options>
                {{ leadLoading ? 'Searching…' : 'No open leads found — everything assigned to you already has an opportunity' }}
              </template>
            </v-select>
            <small v-if="selectedLead" class="text-muted">
              {{ selectedLead.raw.contactPerson || 'No contact person' }} · {{ selectedLead.raw.source || 'Unknown source' }}
            </small>
          </div>

          <!-- Client / Facility — chosen from the real ERP records -->
          <div v-else class="crm-form-group">
            <label class="crm-form-label">Company / Facility <span class="crm-required">*</span></label>
            <v-select
              v-model="selectedCompany"
              :options="companyOptions"
              label="label"
              :filterable="false"
              placeholder="Type to search companies…"
              class="crm-vselect"
              @search="onCompanySearch"
              @input="onCompanyPicked"
            >
              <template #no-options>
                {{ companyLoading ? 'Searching…' : 'Type at least 2 letters to search' }}
              </template>
            </v-select>
            <small v-if="selectedCompany" class="text-muted">
              Facility #{{ selectedCompany.companyId }}
            </small>
          </div>

          <!-- Contact person for that company -->
          <div v-if="!isDeals" class="crm-form-group">
            <label class="crm-form-label">Contact person</label>
            <v-select
              v-model="selectedContact"
              :options="contactOptions"
              label="label"
              :disabled="!selectedCompany"
              placeholder="Select the contact…"
              class="crm-vselect"
              @input="onContactPicked"
            >
              <template #no-options>
                {{ selectedCompany ? 'No contacts recorded for this company' : 'Pick a company first' }}
              </template>
            </v-select>
          </div>

          <!-- Service / product — an opportunity is defined by WHAT we are selling -->
          <div class="crm-form-row" v-if="!isTickets">
            <div class="crm-form-group">
              <label class="crm-form-label">Product / service <span class="crm-required">*</span></label>
              <select class="crm-select" v-model="form.serviceScheme" @change="onSchemeChange">
                <option :value="null">— select product —</option>
                <option v-for="s in schemeOptions" :key="s" :value="s">{{ s }}</option>
              </select>
            </div>
            <div class="crm-form-group">
              <label class="crm-form-label">Action type</label>
              <select class="crm-select" v-model="form.type">
                <option value="new">New</option>
                <option value="renewal">Renewal</option>
                <option value="extend">Extend</option>
                <option value="survey">Survey</option>
              </select>
            </div>
          </div>

          <div class="crm-form-row" v-if="!isTickets && form.serviceScheme">
            <div class="crm-form-group" style="flex:1 1 100%">
              <label class="crm-form-label">
                Regulation / scope
                <small class="text-muted">(links the opportunity to the ERP certificate)</small>
              </label>
              <select class="crm-select" v-model="form.productId" @change="onProductChange">
                <option :value="null">— any / not specified —</option>
                <option v-for="p in productsForScheme" :key="p.id" :value="p.id">
                  {{ p.regulation || p.certificateName }}<span v-if="p.actionType"> · {{ p.actionType }}</span>
                </option>
              </select>
            </div>
          </div>

          <div class="crm-form-row" v-if="!isTickets">
            <div class="crm-form-group">
              <label class="crm-form-label">Expected close date</label>
              <input type="date" class="crm-input" v-model="form.expectedCloseDate" />
            </div>
            <div class="crm-form-group">
              <label class="crm-form-label">Currency</label>
              <select class="crm-select" v-model="form.currency">
                <option value="AED">AED</option>
                <option value="SAR">SAR</option>
                <option value="USD">USD</option>
                <option value="EGP">EGP</option>
              </select>
            </div>
          </div>

          <!-- Two column row -->
          <div class="crm-form-row">
            <!-- Value — auto-calculated from the price list for the chosen product -->
            <div class="crm-form-group" v-if="showValue">
              <label class="crm-form-label">
                Value ({{ form.currency || 'AED' }})
                <small v-if="priceLoading" class="text-muted">calculating…</small>
                <small v-else-if="priceInfo && !priceInfo.isProvisional" class="crm-price-hint">
                  base {{ priceInfo.currency || '' }} {{ money(priceInfo.basicPrice) }}
                  ({{ priceInfo.lineCount }} fee{{ priceInfo.lineCount === 1 ? '' : 's' }})
                </small>
                <!-- MID-CRM P3.3: a tiered fee has no single price. Show the RANGE
                     and make the rep choose, instead of quietly quoting the floor. -->
                <small v-else-if="priceInfo" class="crm-price-hint crm-price-hint--warn">
                  {{ priceInfo.currency || '' }} {{ money(priceInfo.priceRange.low) }}–{{ money(priceInfo.priceRange.high) }}
                  · choose a tier below
                </small>
              </label>
              <input
                type="number"
                class="crm-input"
                v-model.number="form.value"
                placeholder="0"
                min="0"
              />
              <div v-if="priceInfo && priceInfo.lines && priceInfo.lines.length" class="crm-price-lines">
                <div v-for="l in priceInfo.lines" :key="l.priceId" class="crm-price-line">
                  <span>
                    {{ l.activity }}
                    <!-- P3.2: say which list this number came from. -->
                    <em v-if="l.source === 'office_override'" class="crm-price-src" title="This office overrides the country price">office price</em>
                    <em v-else-if="l.country" class="crm-price-src" :title="`Country price list: ${l.country}`">{{ l.country }}</em>
                  </span>

                  <!-- Tiered: the rep picks; nothing is assumed. -->
                  <select
                    v-if="l.tiered"
                    class="crm-input crm-input--sm crm-price-tier"
                    :value="tierChoice[l.activity] || ''"
                    @change="chooseTier(l, $event.target.value)"
                  >
                    <option value="">— choose tier —</option>
                    <option v-for="o in l.options" :key="o.priceId" :value="o.priceId">
                      {{ money(o.amount) }}{{ o.remarks ? ' · ' + o.remarks : '' }}
                    </option>
                  </select>
                  <strong v-else>{{ l.amount !== null ? money(l.amount) : '—' }}</strong>
                </div>

                <small v-if="priceInfo.isProvisional && pendingTiers.length" class="text-danger d-block mt-1">
                  Choose a tier for: {{ pendingTiers.join(', ') }} — until then this is
                  the lowest possible price, not a quote.
                </small>

                <button
                  type="button"
                  class="crm-price-reset"
                  :disabled="priceInfo.isProvisional && pendingTiers.length > 0"
                  @click="applyBasePrice"
                >
                  {{ priceInfo.isProvisional ? 'Apply chosen price' : 'Reset to base price' }}
                </button>
              </div>
            </div>

            <!-- Remaining Days (renewals) -->
            <div class="crm-form-group" v-if="isRenewals">
              <label class="crm-form-label">Expiry Days Remaining</label>
              <input
                type="number"
                class="crm-input"
                v-model.number="form.remainingDays"
                placeholder="0"
                min="0"
              />
            </div>

            <!-- Priority -->
            <div class="crm-form-group">
              <label class="crm-form-label">Priority</label>
              <select class="crm-select" v-model="form.priority">
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>
          </div>

          <!--
            BRD §3.2 (ledger G-13) — Campaign, Source and CR Number are REQUIRED
            fields on a Deal; "Is Broker?" decides who the quotation is addressed
            to (§4.2). Hidden for tickets, which are not deals. Persisted by both
            paths (PipelineCRM.vue submitAddCard() and CrmPipelineController::store(),
            or buildOpportunity() for the lead-conversion path below) — when a lead
            is selected these default from the lead itself and are shown here only
            as an editable override, not a blank field to fill from scratch.
          -->
          <div class="crm-form-row" v-if="!isTickets">
            <div class="crm-form-group">
              <label class="crm-form-label">Campaign <span class="crm-required">*</span></label>
              <input type="text" class="crm-input" v-model="form.campaign" placeholder="e.g. Saber Q3 2026" />
            </div>
            <div class="crm-form-group">
              <label class="crm-form-label">Source <span class="crm-required">*</span></label>
              <select class="crm-select" v-model="form.source">
                <option :value="null">— select —</option>
                <option v-for="s in sourceOptions" :key="s" :value="s">{{ s }}</option>
              </select>
            </div>
          </div>

          <div class="crm-form-row" v-if="!isTickets">
            <div class="crm-form-group">
              <label class="crm-form-label">CR number <span class="crm-required">*</span></label>
              <input type="text" class="crm-input" v-model="form.crNumber" placeholder="Commercial registration no." />
            </div>
            <div class="crm-form-group">
              <label class="crm-form-label">Facility or broker?</label>
              <select class="crm-select" v-model="form.isBroker">
                <option :value="null">— select —</option>
                <option :value="false">The facility itself</option>
                <option :value="true">A broker / agent</option>
              </select>
            </div>
          </div>

          <!--
            A prompt, not a gate: `canSubmit` is deliberately unchanged so this
            component still blocks on exactly what it blocked on before (title,
            a real company, and a product). Blocking on the three BRD-required
            fields while the save path still drops them would trap the rep.
          -->
          <p v-if="!isTickets && brdMissing" class="crm-field-hint">
            {{ brdMissing }}
          </p>

          <!-- Two column row -->
          <div class="crm-form-row">
            <!-- Stage -->
            <div class="crm-form-group">
              <label class="crm-form-label">Stage</label>
              <select class="crm-select" v-model="form.stage">
                <option
                  v-for="stage in stages"
                  :key="stage.id"
                  :value="stage.id"
                >{{ stage.name }}</option>
              </select>
            </div>

            <!-- Due Date -->
            <div class="crm-form-group">
              <label class="crm-form-label">Due Date</label>
              <flat-pickr
                v-model="form.dueDate"
                class="crm-input"
                :config="dateConfig"
                placeholder="Select date…"
              />
            </div>
          </div>

          <!-- Assignee -->
          <div class="crm-form-group">
            <label class="crm-form-label">Assignee</label>
            <v-select
              v-model="selectedOwner"
              :options="team"
              label="name"
              placeholder="Select assignee…"
              class="crm-vselect"
              @input="onOwnerPicked"
            >
              <template #no-options>Loading the sales team…</template>
              <!-- Karim, 2 Sep: role + office next to each name when assigning. -->
              <template #option="member">
                {{ memberLabel(member) }}
              </template>
            </v-select>
          </div>

          <!-- Labels -->
          <div class="crm-form-group">
            <label class="crm-form-label">Labels</label>
            <v-select
              v-model="form.labels"
              :options="labelOptions"
              multiple
              placeholder="Select labels…"
              class="crm-vselect"
              label="name"
            >
              <template #option="{ name, color }">
                <span class="crm-label-dot" :style="{ backgroundColor: color }"></span>
                {{ name }}
              </template>
              <template #selected-option="{ name, color }">
                <span class="crm-label-dot" :style="{ backgroundColor: color }"></span>
                {{ name }}
              </template>
            </v-select>
          </div>

          <!-- MID-CRM P1.5 — "Initial Checklist Items" removed. Everything typed
               there was dropped the moment the card was saved: crm_cards has no
               checklist column and no endpoint accepts one. Asking a rep to plan
               sub-tasks and then binning them is worse than not asking. -->

          <!-- Description -->
          <div class="crm-form-group">
            <label class="crm-form-label">Description</label>
            <textarea
              class="crm-textarea"
              v-model="form.description"
              placeholder="Add a description…"
              rows="3"
            ></textarea>
          </div>
        </div>

        <!-- Footer -->
        <div class="crm-modal-footer">
          <button class="crm-btn crm-btn--ghost" @click="$emit('close')">Cancel</button>
          <button
            class="crm-btn crm-btn--primary"
            :disabled="!canSubmit"
            @click="handleSubmit"
          >
            <feather-icon icon="PlusIcon" size="15" />
            Create Card
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
/* eslint-disable */
import flatPickr from 'vue-flatpickr-component'
import vSelect from 'vue-select'
import axios from 'axios'
import decryptData from '@/store/auth/decryptData'

export default {
  name: 'CardAddModal',
  components: {
    flatPickr,
    vSelect,
  },
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    activePipeline: {
      type: String,
      required: true,
    },
    activePipelineName: {
      type: String,
      required: true,
    },
    // Karim, 7 Sep: "there is some leads that is not assigned" (really: not
    // assigned to whoever is testing) — the lead picker below used to always
    // narrow to the current user's own leads, full stop, even for someone
    // with team/company-wide CRM access (manager, admin, or the new
    // crm-marketing-manager permission). Passed down from PipelineCRM.vue's
    // own `access` (populated from the server, same scope CrmAccess computes).
    access: {
      type: Object,
      default: () => ({ scope: 'own', canViewTeam: false, seesAll: false }),
    },
    stages: {
      type: Array,
      required: true,
    },
    teamMembers: {
      type: Array,
      default: () => [],
    },
    availableLabels: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      form: this.getEmptyForm(),
      products: [],
      schemeOptions: [],
      // company / contact pickers (real ERP records)
      selectedCompany: null,
      selectedContact: null,
      companyOptions: [],
      contactOptions: [],
      companyLoading: false,
      companySearchTimer: null,
      // Karim, 7 Sep: "pull items from the leads, not the customers" — the
      // New Deal picker for the deals pipeline.
      selectedLead: null,
      leadOptions: [],
      leadLoading: false,
      leadSearchTimer: null,
      // sales team (real users, not mock names)
      team: [],
      selectedOwner: null,
      // price from the price list
      priceInfo: null,
      priceLoading: false,
      // MID-CRM P3.3 — the rep's tier choice per activity (priceId + amount).
      tierChoice: {},
      tierAmount: {},
      // BRD §3.2 Source — same list the lead form offers, so a deal created
      // straight on the board is comparable with one that came from a lead.
      sourceOptions: ['Referral', 'Online', 'Scouting', 'Consultant', 'Exhibition', 'Existing Client', 'Other'],
      dateConfig: {
        dateFormat: 'Y-m-d',
        altInput: true,
        altFormat: 'M j, Y',
        allowInput: true,
      },
    }
  },
  computed: {
    /** MID-CRM P3.3 — tiered activities the rep has not decided yet. */
    pendingTiers() {
      if (!this.priceInfo || !this.priceInfo.isProvisional) return []
      return (this.priceInfo.lines || [])
        .filter(l => l.tiered && !this.tierChoice[l.activity])
        .map(l => l.activity)
    },
    isRenewals() {
      return this.activePipeline === 'renewals'
    },
    isTickets() {
      return this.activePipeline === 'tickets'
    },
    isDeals() {
      return this.activePipeline === 'deals'
    },
    productsForScheme() {
      if (!this.form.serviceScheme) return []
      return this.products.filter(p => p.crmName === this.form.serviceScheme)
    },
    showValue() {
      return this.activePipeline === 'deals' || this.activePipeline === 'renewals'
    },
    labelOptions() {
      return this.availableLabels
    },
    canSubmit() {
      // A New Deal must come from a real lead; other pipelines need a real
      // company — either way, not free text.
      const base = this.form.title.trim() && (this.isDeals ? !!this.form.leadId : !!this.form.companyId)
      // An opportunity must say WHAT we are selling.
      return this.isTickets ? base : (base && !!this.form.serviceScheme)
    },
    /**
     * Which of the BRD §3.2 required Deal fields are still empty, phrased so the
     * rep knows why they matter. Advisory only — see the template note.
     */
    brdMissing() {
      const missing = []
      if (!this.form.campaign || !String(this.form.campaign).trim()) missing.push('Campaign')
      if (!this.form.source) missing.push('Source')
      if (!this.form.crNumber || !String(this.form.crNumber).trim()) missing.push('CR number')
      if (!missing.length) return ''
      return `Still missing: ${missing.join(', ')}. Campaign and Source are what the revenue-by-source `
        + 'and marketing ROI reports are built from; the CR number is how this deal is tied to the facility.'
    },
  },
  watch: {
    visible(val) {
      if (val && !this.schemeOptions.length) this.fetchSchemes()
      if (val && !this.team.length) this.fetchTeam()
      if (val) {
        this.selectedCompany = null
        this.selectedContact = null
        this.selectedOwner = null
        this.selectedLead = null
        this.companyOptions = []
        this.contactOptions = []
        this.leadOptions = []
        this.priceInfo = null
        this.tierChoice = {}
        this.tierAmount = {}
      }
      if (val && this.isDeals && !this.leadOptions.length) {
        this.fetchLeads('')
      }
      if (val) {
        this.form = this.getEmptyForm()
        this.$nextTick(() => {
          if (this.$refs.titleInput) {
            this.$refs.titleInput.focus()
          }
        })
      }
    },
  },
  methods: {
    /** Karim, 2 Sep: show role + office next to each name when assigning. */
    memberLabel(member) {
      if (!member) return ''
      const roles = Array.isArray(member.roles) && member.roles.length ? member.roles.join('/') : ''
      const office = member.officeName || 'No office'
      return roles ? `${member.name} (${roles}) — ${office}` : `${member.name} — ${office}`
    },
    money(v) {
      const n = Number(v) || 0
      return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })
    },

    /** Debounced company search against the CRM's mirrored ERP companies. */
    onCompanySearch(term, loading) {
      if (this.companySearchTimer) clearTimeout(this.companySearchTimer)
      if (!term || term.length < 2) { this.companyOptions = []; return }
      this.companyLoading = true
      if (loading) loading(true)
      this.companySearchTimer = setTimeout(async () => {
        try {
          const res = await axios.get('crm/companies', { params: { search: term, limit: 25 } })
          const body = decryptData(res.data.payload)
          const list = (body && body.data && body.data.companies) || []
          this.companyOptions = list.map(x => ({
            label: x.name || `Facility ${x.erp_facility_id}`,
            companyId: x.erp_facility_id,
            raw: x,
          }))
        } catch (e) {
          this.companyOptions = []
        } finally {
          this.companyLoading = false
          if (loading) loading(false)
        }
      }, 300)
    },

    async onCompanyPicked(opt) {
      this.selectedContact = null
      this.contactOptions = []
      this.form.companyId = opt ? opt.companyId : null
      this.form.client = opt ? opt.label : ''
      this.form.clientId = null
      if (!opt) return

      // Pull the contacts (delegators) recorded for this company.
      try {
        const res = await axios.get(`crm/companies/${opt.companyId}`)
        const body = decryptData(res.data.payload)
        const clients = (body && body.data && body.data.clients) || []
        this.contactOptions = clients.map(x => ({
          label: x.name || x.email || `Client ${x.erp_user_id}`,
          clientId: x.erp_user_id,
          ownerId: x.owner_id,
          ownerName: x.owner_name,
        }))
        // If the account already has a responsible salesperson, default to him.
        const withOwner = this.contactOptions.find(x => x.ownerId)
        if (withOwner) this.presetOwner(withOwner.ownerId, withOwner.ownerName)
      } catch (e) {
        this.contactOptions = []
      }
    },

    onContactPicked(opt) {
      this.form.clientId = opt ? opt.clientId : null
      if (opt && opt.ownerId) this.presetOwner(opt.ownerId, opt.ownerName)
    },

    /**
     * Karim, 7 Sep: "the lead list is from the assigned list to the sales and
     * the appearing in the list is the client that still have no opportunity
     * opened." mine=1 — CrmAccess::leads() already restricts a rep to his own,
     * but a manager sees the whole team by default elsewhere on this board;
     * this modal is about MY next call, so it always narrows to the current
     * user regardless of role. notConverted=1 excludes leads that already
     * have an opportunity (CrmLeadController::filteredLeads()).
     */
    async fetchLeads(term) {
      this.leadLoading = true
      try {
        // Karim, 7 Sep: only narrow to "my own leads" for someone who
        // actually has just their own book (a rep). Anyone with team/company
        // visibility (manager, admin, crm-marketing-manager) sees every open,
        // not-yet-converted lead they're allowed to see — same scope
        // CrmAccess already computes server-side for every other CRM list.
        const params = { notConverted: 1, status: 'open', search: term || undefined }
        if (!this.access || !this.access.canViewTeam) {
          params.mine = 1
        }
        const res = await axios.get('crm/leads', { params })
        const body = decryptData(res.data.payload)
        const list = (body && body.data) || []
        this.leadOptions = list.map(l => ({
          label: `${l.title || 'Untitled'} — ${l.client || 'no contact'}`,
          leadId: l.leadId,
          raw: l,
        }))
      } catch (e) {
        this.leadOptions = []
      } finally {
        this.leadLoading = false
      }
    },
    onLeadSearch(term, loading) {
      if (this.leadSearchTimer) clearTimeout(this.leadSearchTimer)
      if (loading) loading(true)
      this.leadSearchTimer = setTimeout(async () => {
        await this.fetchLeads(term)
        if (loading) loading(false)
      }, 300)
    },
    onLeadPicked(opt) {
      this.form.leadId = opt ? opt.leadId : null
      if (!opt) return
      const l = opt.raw
      // Prefill from the lead — all still editable, same as the BRD fields below.
      this.form.title = `${l.title || 'Untitled'} — ${l.scope || 'Opportunity'}`
      this.form.client = l.client || ''
      this.form.companyId = l.companyId || null
      this.form.serviceScheme = l.scope || null
      this.form.serviceId = l.serviceId || null
      this.form.campaign = l.campaign || ''
      this.form.source = l.source || null
      this.form.crNumber = l.crNumber || ''
      this.form.isBroker = l.isBroker ?? null
      if (l.ownerId) this.presetOwner(l.ownerId, l.owner)
    },

    presetOwner(ownerId, ownerName) {
      const found = this.team.find(u => String(u.id) === String(ownerId))
      this.selectedOwner = found || (ownerName ? { id: ownerId, name: ownerName } : null)
      this.form.ownerId = ownerId || null
      this.form.owner = (found && found.name) || ownerName || ''
    },

    onOwnerPicked(u) {
      this.form.ownerId = u ? u.id : null
      this.form.owner = u ? u.name : ''
    },

    async fetchTeam() {
      try {
        const res = await axios.get('crm/team')
        const body = decryptData(res.data.payload)
        this.team = (body && body.data) || []
      } catch (e) { this.team = [] }
    },

    /** Basic price for the chosen product, summed from the price list. */
    async fetchPrice() {
      const scheme = this.form.serviceScheme
      if (!scheme) { this.priceInfo = null; this.tierChoice = {}; this.tierAmount = {}; return }
      this.priceLoading = true
      try {
        const params = { scheme }
        if (this.form.productId) params.productId = this.form.productId
        const res = await axios.get('crm/products/price', { params })
        const body = decryptData(res.data.payload)
        this.priceInfo = (body && body.data) || null
        // Reset BOTH, or a tier chosen for the previous product keeps contributing
        // its amount to the new product's total.
        this.tierChoice = {}
        this.tierAmount = {}
        // Only auto-fill when the rep has not typed a value themselves AND the price
        // is actually decided. MID-CRM P3.3: a tiered fee has no single price, so
        // auto-filling the lowest tier is how reps under-quote without noticing.
        if (this.priceInfo && !this.priceInfo.isProvisional
          && (!this.form.value || this.form.value === 0)) {
          this.applyBasePrice()
        }
      } catch (e) {
        this.priceInfo = null
      } finally {
        this.priceLoading = false
      }
    },

    /** MID-CRM P3.3 — the rep picks a tier; the total follows their choice. */
    chooseTier(line, priceId) {
      if (!priceId) {
        this.$delete(this.tierChoice, line.activity)
      } else {
        const opt = (line.options || []).find(o => String(o.priceId) === String(priceId))
        this.$set(this.tierChoice, line.activity, opt ? opt.priceId : null)
        this.$set(this.tierAmount, line.activity, opt ? opt.amount : 0)
      }
      if (!this.pendingTiers.length) this.applyBasePrice()
    },

    applyBasePrice() {
      if (!this.priceInfo) return
      // Sum the settled lines, using the rep's tier choice where one was made.
      const total = (this.priceInfo.lines || []).reduce((sum, l) => {
        if (l.tiered) return sum + (this.tierAmount[l.activity] || 0)
        return sum + (l.amount || 0)
      }, 0)
      this.form.value = Math.round(total * 100) / 100
      if (this.priceInfo.currency) this.form.currency = this.priceInfo.currency
    },

    async fetchSchemes() {
      try {
        // The product catalog carries the ERP certificate ids, so the opportunity
        // is tied to a real certificate the operations team can issue.
        const res = await axios.get('crm/products')
        const body = decryptData(res.data.payload)
        this.products = (body && body.data && body.data.products) || []
        this.schemeOptions = [...new Set(this.products.map(p => p.crmName).filter(Boolean))].sort()
      } catch (e) { /* optional */ }
    },
    onSchemeChange() {
      this.form.productId = null
      this.form.erpCertificateId = null
      this.fetchPrice()
    },
    onProductChange() {
      const p = this.products.find(x => x.id === this.form.productId)
      this.form.erpCertificateId = p ? p.erpCertificateId : null
      this.fetchPrice()
      if (p && p.actionType) {
        const map = { New: 'new', Renew: 'renewal', Extend: 'extend', Survey: 'survey' }
        this.form.type = map[p.actionType] || this.form.type
      }
    },
    getEmptyForm() {
      return {
        title: '',
        client: '',
        serviceScheme: null,
        serviceId: null,
        productId: null,
        erpCertificateId: null,
        clientId: null,
        companyId: null,
        leadId: null,
        ownerId: null,
        // BRD §3.2 required Deal fields (ledger G-13). isBroker starts null so
        // "not answered" stays distinct from "not a broker".
        campaign: '',
        source: null,
        crNumber: '',
        isBroker: null,
        type: this.activePipeline === 'renewals' ? 'renewal' : 'new',
        currency: 'AED',
        expectedCloseDate: null,
        value: 0,
        remainingDays: 0,
        priority: 'Medium',
        stage: this.stages && this.stages.length ? this.stages[0].id : '',
        dueDate: null,
        owner: '',
        labels: [],
        description: '',
      }
    },
    // MID-CRM P1.5 — addCheckItem() / removeCheckItem() removed with the field.
    handleSubmit() {
      if (!this.canSubmit) return
      this.$emit('submit', { ...this.form })
    },
  },
}
</script>

<style lang="scss" scoped>
$primary: #6366f1;
$primary-dark: #4f46e5;
$success: #10b981;
$warning: #f59e0b;
$danger: #f43f5e;
$slate-50: #f8fafc;
$slate-100: #f1f5f9;
$slate-200: #e2e8f0;
$slate-300: #cbd5e1;
$slate-400: #94a3b8;
$slate-500: #64748b;
$slate-600: #475569;
$slate-700: #334155;
$slate-900: #0f172a;

.crm-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1060;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}

.crm-modal-card {
  background: #fff;
  border-radius: 16px;
  width: 520px;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 60px -12px rgba(0, 0, 0, 0.18), 0 0 0 1px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

.crm-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid $slate-100;

  &__left {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: linear-gradient(135deg, $primary, $primary-dark);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
  }

  &__title {
    font-family: 'Outfit', 'Inter', sans-serif;
    font-size: 16px;
    font-weight: 600;
    color: $slate-900;
    margin: 0;
  }
}

.crm-modal-close {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: $slate-50;
  color: $slate-400;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: $slate-100;
    color: $slate-600;
  }
}

.crm-modal-body {
  padding: 20px 24px;
  overflow-y: auto;
  flex: 1;
}

.crm-modal-footer {
  padding: 16px 24px;
  border-top: 1px solid $slate-100;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.crm-form-group {
  margin-bottom: 18px;
}

.crm-form-row {
  display: flex;
  gap: 14px;

  .crm-form-group {
    flex: 1;
  }
}

.crm-form-label {
  display: block;
  font-family: 'Outfit', 'Inter', sans-serif;
  font-size: 12.5px;
  font-weight: 600;
  color: $slate-600;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.crm-required {
  color: $danger;
}

.crm-input {
  font-family: 'Outfit', 'Inter', sans-serif;
  width: 100%;
  border: 1.5px solid $slate-200;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 13.5px;
  color: $slate-700;
  background: #fff;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;

  &:focus {
    border-color: $primary;
    box-shadow: 0 0 0 3px rgba($primary, 0.1);
  }

  &::placeholder {
    color: $slate-400;
  }
}

.crm-select {
  font-family: 'Outfit', 'Inter', sans-serif;
  width: 100%;
  border: 1.5px solid $slate-200;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 13.5px;
  color: $slate-700;
  background: #fff;
  outline: none;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%2394a3b8' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;

  &:focus {
    border-color: $primary;
    box-shadow: 0 0 0 3px rgba($primary, 0.1);
  }
}

.crm-textarea {
  font-family: 'Outfit', 'Inter', sans-serif;
  width: 100%;
  border: 1.5px solid $slate-200;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 13.5px;
  color: $slate-700;
  background: #fff;
  outline: none;
  resize: vertical;
  min-height: 80px;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;

  &:focus {
    border-color: $primary;
    box-shadow: 0 0 0 3px rgba($primary, 0.1);
  }

  &::placeholder {
    color: $slate-400;
  }
}

.crm-vselect {
  ::v-deep .vs__dropdown-toggle {
    border: 1.5px solid $slate-200;
    border-radius: 10px;
    padding: 6px 10px;
    min-height: 42px;
    transition: border-color 0.2s;
  }

  ::v-deep .vs__dropdown-toggle:has(.vs__open-indicator:focus) {
    border-color: $primary;
  }

  ::v-deep .vs__search {
    font-family: 'Outfit', 'Inter', sans-serif;
    font-size: 13.5px;
    color: $slate-700;
  }

  ::v-deep .vs__dropdown-menu {
    border-radius: 10px;
    border: 1.5px solid $slate-200;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
    padding: 4px;
  }

  ::v-deep .vs__dropdown-option {
    border-radius: 6px;
    padding: 8px 10px;
    font-size: 13.5px;
  }

  ::v-deep .vs__dropdown-option--highlight {
    background: rgba($primary, 0.08);
    color: $primary-dark;
  }
}

.crm-label-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 6px;
  vertical-align: middle;
}

.crm-checklist-add {
  display: flex;
  gap: 8px;

  &__input {
    flex: 1;
  }
}

.crm-checklist-items {
  list-style: none;
  padding: 0;
  margin: 10px 0 0 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.crm-checklist-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  background: $slate-50;
  border-radius: 8px;

  &__icon {
    color: $success;
    flex-shrink: 0;
  }

  &__text {
    flex: 1;
    font-size: 13px;
    color: $slate-700;
  }

  &__remove {
    width: 22px;
    height: 22px;
    border: none;
    background: transparent;
    color: $slate-400;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s;

    &:hover {
      color: $danger;
      background: rgba($danger, 0.08);
    }
  }
}

.crm-btn {
  font-family: 'Outfit', 'Inter', sans-serif;
  font-size: 13.5px;
  font-weight: 600;
  padding: 9px 20px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &--ghost {
    background: $slate-50;
    color: $slate-600;
    border: 1.5px solid $slate-200;

    &:hover {
      background: $slate-100;
    }
  }

  &--primary {
    background: linear-gradient(135deg, $primary, $primary-dark);
    color: #fff;
    box-shadow: 0 2px 8px rgba($primary, 0.3);

    &:hover:not(:disabled) {
      box-shadow: 0 4px 14px rgba($primary, 0.4);
      transform: translateY(-1px);
    }
  }

  &--outline {
    background: #fff;
    color: $slate-500;
    border: 1.5px solid $slate-200;

    &:hover:not(:disabled) {
      border-color: $primary;
      color: $primary;
    }
  }

  &--sm {
    padding: 7px 10px;
  }
}

.crm-modal-fade-enter-active,
.crm-modal-fade-leave-active {
  transition: opacity 0.25s ease;

  .crm-modal-card {
    transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s ease;
  }
}

.crm-modal-fade-enter,
.crm-modal-fade-leave-to {
  opacity: 0;

  .crm-modal-card {
    transform: scale(0.95) translateY(10px);
    opacity: 0;
  }
}

.crm-price-hint { color: #7367f0; font-weight: 600; margin-left: .25rem; }
.crm-price-hint--warn { color: #b26a1b; }
.crm-price-src {
  font-style: normal; font-size: .68rem; font-weight: 700;
  color: #94a3b8; margin-left: .3rem;
}
.crm-price-tier { max-width: 190px; }
.crm-price-lines {
  margin-top: .4rem; border: 1px dashed #e4e4ec; border-radius: .35rem;
  padding: .35rem .5rem; background: #fafaff;
}
.crm-price-line {
  display: flex; justify-content: space-between; font-size: .74rem; color: #6e6b7b; padding: .1rem 0;
  em { font-style: normal; color: #ff9f43; font-size: .68rem; }
}
.crm-field-hint {
  margin: -.4rem 0 1rem; font-size: .74rem; color: #6e6b7b;
  background: #f8f8fb; border-left: 3px solid #ff9f43; border-radius: .25rem; padding: .4rem .55rem;
}
.crm-price-reset {
  border: 0; background: transparent; color: #7367f0; font-size: .72rem;
  font-weight: 600; cursor: pointer; padding: .2rem 0;
}
</style>
